/**
 * Server functions for organization page editing: the representative's "Our
 * pages" editor and the staff review and change history. Representatives
 * name a page by their organization's slug and a program id, and the server
 * checks they represent it. Rules live in `~/lib/server/organization-pages`
 * and `~/lib/server/page-changes`.
 *
 * Aliases: our pages, page editor, send page for review, page change history
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";

import { authMiddleware } from "~/lib/auth-middleware";
import { permissionMiddleware } from "~/lib/permission-middleware";
import { S3Middleware } from "~/lib/s3-middleware";
import {
  logoUploadInputSchema,
  pageTargetSchema,
  savePageDraftInputSchema,
  staffDraftInputSchema,
  type DraftReview,
  type PageEditorData,
  type PageTarget,
} from "~/lib/shared/organization-pages";
import {
  pageChangeFiltersSchema,
  type PageChangeFilters,
  type PageChangeHistory,
} from "~/lib/shared/page-changes";
import type { PageKind } from "~/lib/shared/page-fields";

// ---------------------------------------------------------------------------
// Representatives
// ---------------------------------------------------------------------------

export const getPageEditorFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(pageTargetSchema)
  .handler(async ({ data, context }): Promise<PageEditorData> => {
    const { getPageEditor } = await import("~/lib/server/organization-pages");
    return getPageEditor(context.user.id, data);
  });

export const savePageDraftFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(savePageDraftInputSchema)
  .handler(async ({ data, context }): Promise<PageEditorData> => {
    const { savePageDraft } = await import("~/lib/server/organization-pages");
    const { values, ...target } = data;
    return savePageDraft(context.user.id, target, values);
  });

export const submitPageDraftFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(pageTargetSchema)
  .handler(async ({ data, context }): Promise<PageEditorData> => {
    const { submitPageDraft } = await import("~/lib/server/organization-pages");
    return submitPageDraft(context.user, data);
  });

export const withdrawPageDraftFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(pageTargetSchema)
  .handler(async ({ data, context }): Promise<PageEditorData> => {
    const { withdrawPageDraft } =
      await import("~/lib/server/organization-pages");
    return withdrawPageDraft(context.user.id, data);
  });

export const discardPageDraftFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(pageTargetSchema)
  .handler(async ({ data, context }): Promise<PageEditorData> => {
    const { discardPageDraft } =
      await import("~/lib/server/organization-pages");
    return discardPageDraft(context.user.id, data);
  });

export const getPageLogoUploadUrlFn = createServerFn({ method: "POST" })
  .middleware([S3Middleware, authMiddleware])
  .validator(logoUploadInputSchema)
  .handler(async ({ data, context }) => {
    const { createLogoUploadUrl } =
      await import("~/lib/server/organization-pages");
    const { mimeType, ...target } = data;
    return createLogoUploadUrl(context.user.id, target, mimeType);
  });

// ---------------------------------------------------------------------------
// Staff
// ---------------------------------------------------------------------------

/** The changed fields of a draft, for the admin review card. */
export const getDraftReviewFn = createServerFn({ method: "GET" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(staffDraftInputSchema)
  .handler(async ({ data }): Promise<DraftReview | null> => {
    const { getDraftReview } = await import("~/lib/server/organization-pages");
    return getDraftReview(data.kind, data.draftId);
  });

export const discardDraftAsStaffFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(staffDraftInputSchema)
  .handler(async ({ data, context }): Promise<{ publishedId: string }> => {
    const { discardDraftAsStaff } =
      await import("~/lib/server/organization-pages");
    return discardDraftAsStaff({
      kind: data.kind,
      id: data.draftId,
      actorUserId: context.user.id,
    });
  });

export const listPageChangesFn = createServerFn({ method: "GET" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(pageChangeFiltersSchema)
  .handler(async ({ data }): Promise<PageChangeHistory> => {
    const { listPageChanges } = await import("~/lib/server/page-changes");
    return listPageChanges(data);
  });

export const pageChangeQueryKeys = {
  all: ["page-changes"] as const,
  draftReview: (draftId: string) =>
    ["page-changes", "draft-review", draftId] as const,
  history: (filters: PageChangeFilters) =>
    ["page-changes", "history", filters] as const,
};

export const draftReviewQueryOptions = (kind: PageKind, draftId: string) =>
  queryOptions({
    queryKey: pageChangeQueryKeys.draftReview(draftId),
    queryFn: () => getDraftReviewFn({ data: { kind, draftId } }),
  });

export const pageChangesQueryOptions = (filters: PageChangeFilters) =>
  queryOptions({
    queryKey: pageChangeQueryKeys.history(filters),
    queryFn: () => listPageChangesFn({ data: filters }),
  });

export const pageEditorQueryKey = (target: PageTarget) =>
  ["page-editor", target.slug, target.programId ?? "organization"] as const;

export const pageEditorQueryOptions = (target: PageTarget) =>
  queryOptions({
    queryKey: pageEditorQueryKey(target),
    queryFn: () => getPageEditorFn({ data: target }),
  });
